import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  Stack
} from '@mui/material';
import {
  Add,
  Delete,
  Pause,
  PlayArrow,
  Key,
  Business,
  ContentCopy,
  Edit
} from '@mui/icons-material';
import {
  getTenants,
  createTenant,
  deleteTenant,
  suspendTenant,
  activateTenant,
  regenerateApiKey
} from '@/services/api/tenantService';
import { formatDateOnly } from '@/utils/formatDate';
import type { Tenant, TenantCreate } from '@types';

export const TenantsPage = () => {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // ダイアログ状態
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [apiKeyDialogOpen, setApiKeyDialogOpen] = useState(false);
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [apiKeyInfo, setApiKeyInfo] = useState<{ api_key: string; webhook_secret: string } | null>(null);
  const [suspendReason, setSuspendReason] = useState('');

  // 新規テナント入力
  const [newTenant, setNewTenant] = useState<TenantCreate>({
    code: '',
    name: '',
    owner_email: '',
    plan: 'basic'
  });

  const fetchTenants = async () => {
    try {
      setLoading(true);
      const data = await getTenants();
      setTenants(data.items);
    } catch (err) {
      setError('テナント一覧の取得に失敗しました');
      console.error('Fetch tenants error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTenants();
  }, []);

  const handleCreate = async () => {
    try {
      await createTenant(newTenant);
      setSuccess('テナントを作成しました');
      setCreateDialogOpen(false);
      setNewTenant({ code: '', name: '', owner_email: '', plan: 'basic' });
      fetchTenants();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      setError(error.response?.data?.detail || 'テナントの作成に失敗しました');
    }
  };

  const handleSuspend = async () => {
    if (!selectedTenant) return;
    try {
      await suspendTenant(selectedTenant.id, suspendReason);
      setSuccess(`${selectedTenant.name} を一時停止しました`);
      setSuspendDialogOpen(false);
      setSuspendReason('');
      fetchTenants();
    } catch (err) {
      setError('一時停止に失敗しました');
    }
  };

  const handleActivate = async (tenant: Tenant) => {
    try {
      await activateTenant(tenant.id);
      setSuccess(`${tenant.name} を再開しました`);
      fetchTenants();
    } catch (err) {
      setError('再開に失敗しました');
    }
  };

  const handleRegenerateApiKey = async (tenant: Tenant) => {
    try {
      const result = await regenerateApiKey(tenant.id);
      setApiKeyInfo(result);
      setSelectedTenant(tenant);
      setApiKeyDialogOpen(true);
    } catch (err) {
      setError('APIキーの再生成に失敗しました');
    }
  };

  const handleDelete = async () => {
    if (!selectedTenant) return;
    try {
      await deleteTenant(selectedTenant.id);
      setSuccess(`${selectedTenant.name} を削除しました`);
      setDeleteDialogOpen(false);
      fetchTenants();
    } catch (err) {
      setError('削除に失敗しました');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSuccess('クリップボードにコピーしました');
  };

  const getStatusChip = (tenant: Tenant) => {
    if (tenant.suspended_at) {
      return <Chip label="停止中" color="error" size="small" />;
    }
    if (tenant.is_active === 1) {
      return <Chip label="有効" color="success" size="small" />;
    }
    return <Chip label="無効" color="default" size="small" />;
  };

  const getPlanChip = (plan: string) => {
    const colors: Record<string, 'default' | 'primary' | 'secondary' | 'success'> = {
      basic: 'default',
      pro: 'primary',
      enterprise: 'secondary'
    };
    return <Chip label={plan.toUpperCase()} color={colors[plan] || 'default'} size="small" />;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            テナント管理
          </Typography>
          <Typography variant="body1" color="text.secondary">
            ホワイトラベル/OEMパートナーの管理
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setCreateDialogOpen(true)}
        >
          テナント作成
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>テナント</TableCell>
                  <TableCell>オーナー</TableCell>
                  <TableCell>プラン</TableCell>
                  <TableCell>ステータス</TableCell>
                  <TableCell>作成日</TableCell>
                  <TableCell align="right">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tenants.map((tenant) => (
                  <TableRow key={tenant.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Business color="action" />
                        <Box>
                          <Typography fontWeight="medium">{tenant.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {tenant.code}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography>{tenant.owner_email}</Typography>
                      {tenant.owner_name && (
                        <Typography variant="caption" color="text.secondary">
                          {tenant.owner_name}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{getPlanChip(tenant.plan)}</TableCell>
                    <TableCell>{getStatusChip(tenant)}</TableCell>
                    <TableCell>{formatDateOnly(tenant.created_at)}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="編集・詳細">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => navigate(`/admin/tenants/${tenant.id}`)}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="APIキー再生成">
                          <IconButton
                            size="small"
                            onClick={() => handleRegenerateApiKey(tenant)}
                          >
                            <Key />
                          </IconButton>
                        </Tooltip>
                        {tenant.suspended_at ? (
                          <Tooltip title="再開">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleActivate(tenant)}
                            >
                              <PlayArrow />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="一時停止">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => {
                                setSelectedTenant(tenant);
                                setSuspendDialogOpen(true);
                              }}
                            >
                              <Pause />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="削除">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setSelectedTenant(tenant);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* 新規作成ダイアログ */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>テナント作成</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="テナントコード"
              value={newTenant.code}
              onChange={(e) => setNewTenant({ ...newTenant, code: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
              helperText="半角英数字とハイフンのみ（例: acme, partner-a）"
              fullWidth
              required
            />
            <TextField
              label="テナント名"
              value={newTenant.name}
              onChange={(e) => setNewTenant({ ...newTenant, name: e.target.value })}
              helperText="会社名や表示名"
              fullWidth
              required
            />
            <TextField
              label="オーナーメールアドレス"
              type="email"
              value={newTenant.owner_email}
              onChange={(e) => setNewTenant({ ...newTenant, owner_email: e.target.value })}
              fullWidth
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>キャンセル</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!newTenant.code || !newTenant.name || !newTenant.owner_email}
          >
            作成
          </Button>
        </DialogActions>
      </Dialog>

      {/* APIキー表示ダイアログ */}
      <Dialog open={apiKeyDialogOpen} onClose={() => setApiKeyDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>APIキー発行完了</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            このAPIキーとWebhookシークレットは一度しか表示されません。安全な場所に保管してください。
          </Alert>
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>APIキー</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  value={apiKeyInfo?.api_key || ''}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  size="small"
                />
                <IconButton onClick={() => copyToClipboard(apiKeyInfo?.api_key || '')}>
                  <ContentCopy />
                </IconButton>
              </Box>
            </Box>
            <Box>
              <Typography variant="subtitle2" gutterBottom>Webhookシークレット</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  value={apiKeyInfo?.webhook_secret || ''}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  size="small"
                />
                <IconButton onClick={() => copyToClipboard(apiKeyInfo?.webhook_secret || '')}>
                  <ContentCopy />
                </IconButton>
              </Box>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApiKeyDialogOpen(false)} variant="contained">
            閉じる
          </Button>
        </DialogActions>
      </Dialog>

      {/* 一時停止ダイアログ */}
      <Dialog open={suspendDialogOpen} onClose={() => setSuspendDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>テナント一時停止</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            <strong>{selectedTenant?.name}</strong> を一時停止しますか？
          </Typography>
          <TextField
            label="停止理由"
            value={suspendReason}
            onChange={(e) => setSuspendReason(e.target.value)}
            multiline
            rows={3}
            fullWidth
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuspendDialogOpen(false)}>キャンセル</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleSuspend}
            disabled={!suspendReason}
          >
            一時停止
          </Button>
        </DialogActions>
      </Dialog>

      {/* 削除確認ダイアログ */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>テナント削除</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            この操作は取り消せません。関連するすべてのデータが削除されます。
          </Alert>
          <Typography>
            <strong>{selectedTenant?.name}</strong> を削除しますか？
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>キャンセル</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            削除
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
